<template>
    <tailwind-layout title="Home">
        <div class="mx-auto max-w-4xl">
            <h1 class="text-2xl font-bold text-rent-risk-blue-400 sm:text-3xl">Complete Onboarding</h1>
            <p class="mt-1 text-sm text-gray-600">Managers who completed these steps reported a better overall experience.</p>

            <div
                class="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
                role="progressbar"
                aria-label="Onboarding progress"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="checklist.progress"
            >
                <div class="h-full rounded-full bg-gold-900" :style="{ width: `${checklist.progress}%` }" />
            </div>

            <section
                v-for="section in checklist.sections"
                :key="section.key"
                class="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
            >
                <header class="flex items-start justify-between gap-4 bg-gray-50 px-4 py-4 sm:px-6">
                    <div class="min-w-0">
                        <h2 class="text-xl text-rent-risk-blue-400 sm:text-2xl">{{ section.label }}</h2>
                        <p class="mt-0.5 text-sm text-gray-600">{{ section.description }}</p>
                    </div>
                    <span class="shrink-0 pt-1 text-base text-gray-700" data-test="section-counter">
                        {{ section.completed_count }} of {{ section.total_count }}
                    </span>
                </header>

                <ul class="divide-y divide-gray-200 px-4 sm:px-6">
                    <li
                        v-for="task in section.tasks"
                        :key="task.key"
                        class="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
                        :data-test="`task-${task.key}`"
                    >
                        <div class="flex min-w-0 items-start gap-3" :class="task.done ? 'text-gray-400' : 'text-gray-700'">
                            <!-- Read-only by decision: tasks tick themselves when the landlord does them. -->
                            <span role="img" class="mt-0.5 shrink-0" :aria-label="task.done ? 'Completed' : 'Not completed'">
                                <font-awesome-icon
                                    :icon="task.done ? faSquareCheck : faSquare"
                                    class="h-5 w-5"
                                    :class="task.done ? 'text-green-600' : 'text-gray-500'"
                                    aria-hidden="true"
                                />
                            </span>
                            <div class="min-w-0 text-sm">
                                <p :class="task.done ? 'line-through' : ''">
                                    <span class="font-semibold" :class="task.done ? '' : 'text-rent-risk-blue-400'">{{ task.label }}</span>
                                    {{ task.description }}
                                </p>
                                <p
                                    v-if="task.disabled_reason === 'no_open_property'"
                                    class="mt-1 text-xs text-gray-500"
                                    data-test="no-open-property-hint"
                                >
                                    Reopen or add a property to share your screening link.
                                </p>
                            </div>
                        </div>

                        <template v-if="! task.done">
                            <Link
                                v-if="task.action === 'link'"
                                :href="task.href"
                                :class="ctaClasses(task)"
                                :data-test="`cta-${task.key}`"
                            >
                                {{ task.cta }}
                            </Link>
                            <button
                                v-else-if="task.action === 'share_modal'"
                                type="button"
                                :class="ctaClasses(task)"
                                :disabled="task.disabled || ! latest_property"
                                :data-test="`cta-${task.key}`"
                                @click="shareModalOpen = true"
                            >
                                {{ task.cta }}
                            </button>
                        </template>
                        <span v-else class="text-sm font-medium text-green-700 sm:w-36 sm:text-center">Done</span>
                    </li>
                </ul>
            </section>
        </div>

        <ShareModal
            :show="shareModalOpen"
            :share-url="latest_property?.application_link ?? ''"
            :share-text="''"
            title="Start Screening Tenants"
            :link-only="true"
            @onboarding-task-recorded="rememberRecording"
            @close="closeShareModal"
        />
    </tailwind-layout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSquare, faSquareCheck } from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import ShareModal from '@/Pages/V2/Properties/ShareModal.vue'

/**
 * 86bc2cgxd: the landlord onboarding checklist, shown in place of Home until every
 * task is done. The server owns the tasks; this page switches on each task's
 * `action`, so new tasks and sections need no changes here.
 */
const props = defineProps({
    checklist: {
        type: Object,
        required: true,
    },
    latest_property: {
        type: Object,
        default: null,
    },
})

const shareModalOpen = ref(false)

// The modal records a copy in the background. Once it lands, reload so the task
// ticks here too — or, when that was the last task, so the server sends them Home.
let pendingRecord = null

const rememberRecording = (recording) => {
    pendingRecord = recording
}

const closeShareModal = async () => {
    shareModalOpen.value = false

    if (! pendingRecord) {
        return
    }

    const recording = pendingRecord
    pendingRecord = null
    await recording
    router.reload()
}

// The first task not yet done is the one to do next, so its button is the loud one.
const nextTaskKey = computed(() => {
    for (const section of props.checklist.sections) {
        const next = section.tasks.find((task) => ! task.done && ! task.disabled)

        if (next) {
            return next.key
        }
    }

    return null
})

const ctaClasses = (task) => [
    'inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-bold transition-colors sm:w-36',
    'focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    task.key === nextTaskKey.value
        ? 'bg-gold-900 text-gray-900 hover:bg-[#a8862b]'
        : 'bg-gold-500 text-[#7d6310] hover:bg-gold-700',
]
</script>
